(() => {
  'use strict';
  const $ = selector => document.querySelector(selector);
  const $$ = selector => [...document.querySelectorAll(selector)];
  const base = window.Shopify?.routes?.root || '/';
  const products = Object.fromEntries($$('[data-cart-product]').map(node => [node.dataset.cartProduct, {
    name: node.dataset.name,
    variants: Object.fromEntries(node.dataset.sizes.split(',').map((size, i) => [size, Number(node.dataset.variantIds.split(',')[i])]))
  }]));
  const fashionVariants = new Set(Object.values(products).flatMap(product => Object.values(product.variants)));
  const fashionTypes = new Set(['accessory', 'dress', 'playsuit']);
  const isFashion = item => fashionVariants.has(item.variant_id) || fashionTypes.has((item.product_type || '').trim().toLowerCase());
  const limit = 10;
  let cart = { items: [], item_count: 0, total_price: 0, currency: 'USD' };
  let busy = false;
  let cartVerified = false;
  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: cart.currency }).format(cents / 100);
  const announce = message => { $('[data-cart-status]').textContent = message; };
  const request = async (path, body) => {
    const response = await fetch(base + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Could not update your bag. Please try again.');
    return result;
  };
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const setBusy = value => {
    busy = value;
    $('#bag-dialog').setAttribute('aria-busy', String(value));
    $$('#bag-dialog button:not([data-close]), #fashion-form [type="submit"], [data-scroll-size]').forEach(button => { button.disabled = value; });
    $('[data-cart-checkout]').disabled = value || !cartVerified || !cart.item_count;
  };
  const refresh = async () => {
    cartVerified = false;
    let updated = await request('cart.js');
    const excluded = updated.items.filter(item => !isFashion(item));
    for (const item of excluded) await request('cart/change.js', { id: item.key, quantity: 0 });
    if (excluded.length) updated = await request('cart.js');
    if (updated.items.some(item => !isFashion(item))) throw new Error('Could not prepare your fashion-only bag. Please try again.');
    cart = updated; cartVerified = true; render();
  };
  const mutate = async (path, body, message) => {
    if (busy) return false;
    setBusy(true); announce('Updating your bag…');
    try {
      await request(path, body); await refresh(); announce(message); return true;
    } catch (error) {
      try { await refresh(); } catch { /* Preserve the last confirmed cart. */ }
      announce(error.message || 'Could not update your bag. Please try again.'); return false;
    } finally { setBusy(false); }
  };
  const row = item => {
    const article = make('article', 'cart-line');
    const image = make('img');
    if (item.image) image.src = item.image;
    image.alt = item.product_title; image.width = 100; image.height = 150;
    const info = make('div', 'cart-line-info');
    info.append(make('h3', '', item.product_title), make('p', 'cart-meta', item.variant_title || ''));
    if (item.selling_plan_allocation) info.append(make('p', 'cart-meta', item.selling_plan_allocation.selling_plan.name));
    const controls = make('div', 'quantity-controls');
    const change = async quantity => {
      const updated = await mutate('cart/change.js', { id: item.key, quantity }, quantity ? 'Bag updated.' : item.product_title + ' removed.');
      if (updated) $('#bag-dialog [data-close]').focus();
    };
    for (const [text, delta, label] of [['−', -1, 'Decrease quantity'], ['+', 1, 'Increase quantity']]) {
      const button = make('button', '', text); button.type = 'button';
      button.setAttribute('aria-label', label + ' ' + item.product_title + ' ' + (item.variant_title || ''));
      button.addEventListener('click', () => {
        if (delta > 0 && item.quantity >= limit) { announce('Maximum ' + limit + ' per selection.'); return; }
        change(item.quantity + delta);
      });
      controls.append(button);
      if (delta < 0) controls.append(make('span', '', String(item.quantity)));
    }
    const remove = make('button', 'inline-link', 'Remove'); remove.type = 'button';
    remove.setAttribute('aria-label', 'Remove ' + item.product_title + ' ' + (item.variant_title || ''));
    remove.addEventListener('click', () => change(0));
    info.append(controls, remove);
    article.append(image, info, make('strong', 'cart-line-price', money(item.final_line_price)));
    return article;
  };
  const render = () => {
    $('[data-bag-count]').textContent = String(cart.item_count);
    $('[data-cart-count]').textContent = String(cart.item_count);
    $('[data-bag-empty]').hidden = cart.item_count > 0;
    $('[data-bag-filled]').hidden = cart.item_count === 0;
    $('[data-cart-lines]').replaceChildren(...cart.items.map(row));
    $('[data-cart-total]').textContent = money(cart.total_price);
    $('[data-cart-currency]').textContent = cart.currency;
    setBusy(busy);
  };
  const add = async (id, size) => {
    await ready;
    if (busy) return false;
    const variant = products[id]?.variants[size];
    if (!Number.isSafeInteger(variant) || variant <= 0) { announce('Please choose an available size.'); return false; }
    const quantity = cart.items.filter(item => item.variant_id === variant).reduce((sum, item) => sum + item.quantity, 0);
    if (quantity >= limit) { announce('Maximum ' + limit + ' per selection.'); return false; }
    return mutate('cart/add.js', { items: [{ id: variant, quantity: 1 }] }, products[id].name + ', size ' + size + ', added to your bag.');
  };
  $$('[data-upsell-add]').forEach(button => button.addEventListener('click', () => {
    const card = button.closest('[data-cart-product]');
    add(card.dataset.cartProduct, card.querySelector('select').value);
  }));
  const syncBag = async () => {
    if (busy) return;
    setBusy(true);
    try { await refresh(); }
    catch { announce('Could not prepare your fashion-only bag. Please try again.'); }
    finally { setBusy(false); }
  };
  $$('[data-open="bag-dialog"]').forEach(button => button.addEventListener('click', syncBag));
  window.addEventListener('pageshow', event => { if (event.persisted) syncBag(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') syncBag(); });
  $('[data-cart-checkout]').addEventListener('click', async () => {
    if (busy || !cartVerified || !cart.item_count) return;
    setBusy(true);
    try {
      await refresh();
      if (!cart.item_count) throw new Error('Your bag is empty. Add an item before checkout.');
      if (!window.__checkoutInterceptInstalled) throw new Error('Checkout is still loading. Please try again in a moment.');
      announce('Opening secure checkout…');
      const link = make('a'); link.href = base + 'checkout'; link.hidden = true;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { announce(error.message || 'Could not open checkout. Please try again.'); }
    finally { setBusy(false); }
  });
  setBusy(true);
  const ready = refresh().catch(() => announce('Could not load your bag. Please reload or try adding an item.')).finally(() => setBusy(false));
  window.MercerCart = { add, ready };
})();
